import React, { useEffect, useRef, useState } from 'react'
import { Badge, Box } from '@mui/material'
import { ButtonCustom } from '../Button/ButtonCustom'
import { useStyles } from '../../pages/EventReview/style'
import { Event, EventType, User } from '../../services/Types'
import { useAllEvents } from '../../hooks/useAllEvents'
import { usePermissions } from '../../hooks/usePermissions'
import { useNavigate, useParams } from 'react-router-dom'
import { eventCompleted } from '../../services/EventService'
import { useEvent } from '../../hooks/useEvent'
import { ProcessingDialog } from '../InfoDialogs'
import { CustomDialog } from '../CustomDialog'
import { DialogCancelEvent } from '../Dialogs/Cancel'
import { DialogRejectEvent } from '../Dialogs/Reject'
import { DialogUnlockEvent } from '../Dialogs/Unlock'
import { DialogApproveEvent } from '../Dialogs/Approve'
import { DialogDeleteEvent } from '../Dialogs/Delete'
import { DialogTransferRequester } from '../Dialogs/TransferRequester'
import { useEventValidation } from '../../contexts/EventValidationContext'
import { useEventCommentsCount } from '../../hooks/useEventCommentsCount'
import { useSidebarLayout } from '../../hooks/useSidebarLayout'
import { canUserAccessEventComments } from '../../utils/eventCommentsAccess'
import {
  getSidebarParam,
  openEventComments,
  openVersionHistory
} from '../../utils/sidebarUrl'

interface FooterEventEditProps {
  eventData: Event
  setServerError: (error: string) => void
  setServerInfo: (info: string) => void
  setShowSuccessMessage: (show: boolean) => void
}

export const FooterEventEdit: React.FC<FooterEventEditProps> = props => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { id } = useParams()
  const user = JSON.parse(localStorage.getItem('user') || '{}') as User
  const { sidebarWidth } = useSidebarLayout()
  const canAccessComments = canUserAccessEventComments(user)
  const { count: commentsCount } = useEventCommentsCount(
    props.eventData.id,
    canAccessComments
  )
  const autoOpenedRef = useRef(false)
  const { getSeriesParent } = useAllEvents();
  const { 
    eventReadyToApprove,
    eventIsBlocked,
    // Button visibility variables
    showButtonUnlock,
    showButtonSave,
    showButtonApprove,
    showButtonReject,
    showButtonBack,
    showButtonSaveAndSubmit,
    showButtonViewSeriesParent,
    disableButtonSaveAndSubmit,
    showButtonCancel,
    showButtonDelete,
    showButtonTransferRequester,
    userIsAdmin
  } = usePermissions(props.eventData);
  
  // Get validation state to disable save buttons when there are errors
  const { hasEventValidationErrors } = useEventValidation();
  
  const {
    updateDbEvent,
    initSubmitEvent,
  } = useEvent();
  const [showDialog, setDialog] = useState('')
  const [isProcessing, setProcessing] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [showUnlockDialog, setShowUnlockDialog] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTransferDialog, setShowTransferDialog] = useState(false)

  const approveText = props.eventData.eventType === EventType.SERIES ? "Approve All" : "Approve"
  const rejectText = props.eventData.eventType === EventType.SERIES ? "Reject All Series Events" : "Send back to requester"
  const seriesParent = getSeriesParent(props.eventData.seriesId)

  // Get enable/disable state from context
  const { isFormEnabled, toggleFormEnabled } = useEventValidation();

  useEffect(() => {
    if (
      !canAccessComments ||
      !props.eventData.id ||
      commentsCount < 1 ||
      autoOpenedRef.current
    ) {
      return
    }
    if (getSidebarParam('history') || getSidebarParam('comments')) {
      autoOpenedRef.current = true
      return
    }
    autoOpenedRef.current = true
    openEventComments(props.eventData.id)
  }, [canAccessComments, commentsCount, props.eventData.id])

  const handleBackClick = async () => {
    if (props.eventData.eventType === EventType.SERIES) navigate('/event/dateAndTime/' + id)
    else navigate('/event/featuresAndRequests/' + id)
  }

  const handleSaveEvent = async () => {
    if (!props.eventData) return;
    setProcessing(true)
    try {
      const updateReturn = await updateDbEvent(props.eventData)
      if (!updateReturn) throw new Error('Error updating event')
      props.setServerInfo('Event saved')
      props.setShowSuccessMessage(true)
    } catch (error) {
      console.error(error)
      props.setServerError('Error saving event: ' + error)
    } finally {
      setProcessing(false)
    }
  }

  const handleSubmitEvent = async () => {
    if (!props.eventData) return;
    setProcessing(true)
    try {
      await handleSaveEvent()
      await initSubmitEvent(props.eventData)
      props.setServerInfo('Event updated')
      props.setShowSuccessMessage(true)
      navigate(0)
    } catch (error) {
      console.error(error)
      props.setServerError('Error completing event: ' + error)
    } finally {
      setProcessing(false)
    }
  }

  // TODO: refactor - stop using ButtonCustom and DialogCustom just use theme buttons and dialogs
  return (
    <div
      className={classes.footerButton}
      style={{
        right: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
        transition: 'right 225ms cubic-bezier(0, 0, 0.2, 1), width 225ms cubic-bezier(0, 0, 0.2, 1)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          background: 'white',
          pl: 5,
          pr: 5
        }}
      >
      <Box sx={{ flex: '1 1 auto' }} />
        <ButtonCustom
          variant='contained'
          onClick={() => openVersionHistory(props.eventData.id)}
          label='Version History'
          icon={false}
        />

        {canAccessComments && (
          <Badge
            badgeContent={commentsCount > 0 ? Math.min(commentsCount, 99) : 0}
            color="error"
            invisible={commentsCount < 1}
            overlap="rectangular"
            sx={{
              '& .MuiBadge-badge': {
                color: '#fff',
                backgroundColor: '#d32f2f',
                fontWeight: 700,
                fontSize: '0.7rem',
                minWidth: 18,
                height: 18,
                // Sit on the pill corner so ~half the badge covers the button
                right: 10,
                top: 4,
                transform: 'none'
              }
            }}
          >
            <ButtonCustom
              variant='contained'
              onClick={() => openEventComments(props.eventData.id)}
              label='Comments'
              icon={false}
            />
          </Badge>
        )}

        { showButtonViewSeriesParent &&
          <ButtonCustom
            variant='contained'
            onClick={() => window.open(`/event/review/${seriesParent?.id}`, '_blank', 'noopener noreferrer')}
            label='View series parent'
            icon={false}
          />
        }

        {showButtonSave && (
          <ButtonCustom
            variant='contained'
            onClick={toggleFormEnabled}
            label={isFormEnabled ? 'Disable Form' : 'Enable Form'}
            icon={false}
          />
        )}

        {showButtonCancel && (
          <ButtonCustom
            variant='contained'
            onClick={() => setDialog('Cancel')}
            label='Cancel'
            icon={false}
          />
        )}
        {showButtonTransferRequester && (
          <ButtonCustom
            variant='contained'
            onClick={() => setShowTransferDialog(true)}
            label='Transfer'
            icon={false}
          />
        )}
        {showButtonDelete && eventIsBlocked && (
          <ButtonCustom
            variant='contained'
            onClick={() => setShowDeleteDialog(true)}
            label='Delete'
            icon={false}
          />
        )}
        {showButtonSave && (
          <ButtonCustom
            variant='contained'
            onClick={handleSaveEvent}
            label='Save'
            icon={false}
            disabled={hasEventValidationErrors()}
          />
        )}

        {showButtonReject && (
          <ButtonCustom
            variant='contained'
            onClick={() => setShowRejectDialog(true)}
            label={rejectText}
            icon={false}
          />
        )}

        {showButtonApprove && (
          <ButtonCustom
            variant='contained'
            onClick={() => setShowApproveDialog(true)}
            label={approveText}
            disabled={isProcessing || !eventReadyToApprove}
            icon={false}
          />
        )}

        {showButtonUnlock && (
          <ButtonCustom
            variant='contained'
            onClick={() => setShowUnlockDialog(true)}
            label='Unlock'
            icon={false}
          />
        )}

        {showButtonBack && (
          <ButtonCustom
            variant='contained'
            onClick={handleBackClick}
            label='Back'
            icon={false}
          />
        )}

        {showButtonSaveAndSubmit && (
          <ButtonCustom
            variant='contained'
            icon={false}
            disabled={isProcessing || disableButtonSaveAndSubmit || hasEventValidationErrors()}
            onClick={handleSubmitEvent}
            label='Save and submit'
          />
        )}
      </Box>

      {showDialog === 'Cancel' && (
        <DialogCancelEvent
          eventId={props.eventData.id}
          onSuccess={() => {
            props.setServerInfo('Event cancelled')
            props.setShowSuccessMessage(true)
            navigate(0)
          }}
          onCancel={() => setDialog('')}
        />
      )}

      {showRejectDialog && (
        <DialogRejectEvent
          eventId={props.eventData.id}
          onSuccess={() => {
            props.setServerInfo('Event rejected')
            props.setShowSuccessMessage(true)
            navigate(0)
          }}
          onCancel={() => setShowRejectDialog(false)}
        />
      )}

      {showUnlockDialog && (
        <DialogUnlockEvent
          eventId={props.eventData.id}
          onSuccess={() => {
            props.setServerInfo('Event unlocked')
            props.setShowSuccessMessage(true)
            navigate(0)
          }}
          onCancel={() => setShowUnlockDialog(false)}
        />
      )}

      {showApproveDialog && (
        <DialogApproveEvent
          eventId={props.eventData.id}
          // onSuccess={async () => {
          //   await handleSaveEvent()
          onSuccess={() => {
            props.setServerInfo('Event approved')
            props.setShowSuccessMessage(true)
            navigate(0)
          }}
          onCancel={() => setShowApproveDialog(false)}
        />
      )}

      {showDeleteDialog && (
        <DialogDeleteEvent
          eventId={props.eventData.id}
          onSuccess={() => {
            props.setServerInfo('Event deleted')
            props.setShowSuccessMessage(true)
            // Navigate to admin event list or home page after deletion
            navigate('/admin/event')
          }}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}

      {showTransferDialog && (
        <DialogTransferRequester
          eventData={props.eventData}
          onSuccess={(newRequester) => {
            props.setServerInfo(`Event transferred to ${newRequester.firstName} ${newRequester.lastName}`)
            props.setShowSuccessMessage(true)
            setShowTransferDialog(false)
            if (userIsAdmin) {
              navigate(0)
            } else {
              navigate('/')
            }
          }}
          onCancel={() => setShowTransferDialog(false)}
        />
      )}

      <ProcessingDialog open={isProcessing} />

    </div>
  )
} 